import { useEffect, useRef, useState } from 'react';
import { useCachedQuery } from '../../shared/lib/queryCache';
import { RefreshingHint } from '../../shared/ui/RefreshingHint';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { ExternalLink, Calendar, Eye, Heart, MessageCircle, Share2, Bookmark, Send, ChevronLeft, ChevronRight, TrendingUp, Loader2, MessagesSquare } from 'lucide-react';
import { MediaThumb } from '../../shared/ui/MediaThumb';
import { PlatformBadge } from '../../features/statistics/ui/PlatformBadge';
import { allPublicationsApi, type AllPublicationItem, type AllPublicationInsights, type PublicationsPlatform } from '../../shared/api/all-publications.api';
import { ACCOUNT_PLATFORMS } from '../../entities/account';
import { toast } from 'sonner';
import { Skeleton } from '../../components/ui/skeleton';
import { commentsLink, pageFromParam, runLimited } from '../publications/lib/journal';
import { stashFeedItem } from './feedItemStash';

function formatDate(dateString: string) {
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${day}.${month}.${year} ${hours}:${minutes}`;
}

function formatNumber(num: number) {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num.toString();
}

/** Ключи кэша ленты: платформа и страница. */
const FEED_CACHE_PREFIX = 'all-publications:';

const PLATFORMS: readonly PublicationsPlatform[] = ['all', 'instagram', 'tiktok'];

/** Метрики видимой страницы подтягиваем фоном кусками — строки заполняются по мере ответа. */
const INSIGHTS_CHUNK = 10;
const INSIGHTS_CONCURRENCY = 2;

export function AllPublicationsPage() {
  const navigate = useNavigate();
  // Страница и платформа — в адресе: «Назад» из поста возвращает туда же.
  const [params, setParams] = useSearchParams();
  const currentPage = pageFromParam(params.get('page'));
  const rawPlatform = params.get('platform') as PublicationsPlatform | null;
  const platform: PublicationsPlatform = rawPlatform && PLATFORMS.includes(rawPlatform) ? rawPlatform : 'all';
  const [refreshing, setRefreshing] = useState(false);
  const [insightsMap, setInsightsMap] = useState<Record<string, AllPublicationInsights>>({});

  // Серверная пагинация поверх таблицы-кэша instagram_media (page-based).
  // Страницу, которую уже открывали, показываем из кэша и обновляем следом.
  const {
    data: feed,
    error: loadError,
    isLoading,
    isRefreshing,
  } = useCachedQuery(
    `${FEED_CACHE_PREFIX}${platform}:${currentPage}`,
    () => allPublicationsApi.getFeed(currentPage, platform),
  );

  useEffect(() => {
    if (loadError) toast.error('Ошибка загрузки публикаций');
  }, [loadError]);

  const items = feed?.publications ?? [];
  const totalCount = feed?.totalCount ?? 0;
  const totalPages = feed?.totalPages ?? 1;
  const hasNextPage = feed?.hasMore ?? false;

  const writeParams = (page: number, pf: PublicationsPlatform) => {
    const next = new URLSearchParams(params);
    if (page <= 1) next.delete('page'); else next.set('page', String(page));
    if (pf === 'all') next.delete('platform'); else next.set('platform', pf);
    setParams(next);
  };
  const goToPage = (page: number) => writeParams(page, platform);
  // Смена платформы — всегда сбрасываем на первую страницу.
  const changePlatform = (pf: PublicationsPlatform) => writeParams(1, pf);

  /** Метрики (views/shares/saved/ER) для постов: кусками, несколько кусков разом. */
  const loadInsights = async (list: AllPublicationItem[]): Promise<number> => {
    const chunks: AllPublicationItem[][] = [];
    for (let i = 0; i < list.length; i += INSIGHTS_CHUNK) chunks.push(list.slice(i, i + INSIGHTS_CHUNK));
    let count = 0;
    await runLimited(chunks, INSIGHTS_CONCURRENCY, async (chunk) => {
      const map = await allPublicationsApi.getInsights(
        chunk.map((it) => ({ mediaId: it.mediaId, accountId: it.accountId, mediaType: it.mediaType, likes: it.likes, comments: it.comments })),
      );
      count += Object.keys(map).length;
      setInsightsMap((prev) => ({ ...prev, ...map }));
    });
    return count;
  };

  // Видимая страница — фоном, один раз на пост; кнопка — принудительно заново.
  const autoLoaded = useRef(new Set<string>());
  useEffect(() => {
    const fresh = items.filter((it) => !autoLoaded.current.has(it.mediaId));
    if (!fresh.length) return;
    fresh.forEach((it) => autoLoaded.current.add(it.mediaId));
    void loadInsights(fresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.map((it) => it.mediaId).join(',')]);

  const handleRefreshInsights = async () => {
    if (items.length === 0 || refreshing) return;
    try {
      setRefreshing(true);
      const count = await loadInsights(items);
      toast.success(`Обновлено метрик: ${count}`);
    } finally {
      setRefreshing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="p-6">
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <CardTitle>Вся лента аккаунтов</CardTitle>
              <CardDescription className="flex flex-wrap items-center gap-2">
                {totalCount === 0
                  ? 'Нет публикаций'
                  : `Всё, что есть в аккаунтах — ${totalCount.toLocaleString('ru-RU')} публикаций (страница ${currentPage} из ${totalPages})`}
                <RefreshingHint active={isRefreshing} />
              </CardDescription>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {/* Платформы из реестра: новая сеть = +1 кнопка */}
              <div className="inline-flex overflow-hidden rounded-lg border p-0.5">
                {([{ id: 'all' as const, label: 'Все' }, ...ACCOUNT_PLATFORMS]).map(({ id, label }) => (
                  <button
                    key={id}
                    className={`rounded-md px-3 py-1.5 text-sm transition-colors ${
                      platform === id
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-transparent text-muted-foreground hover:text-foreground'
                    }`}
                    onClick={() => changePlatform(id)}
                  >{label}</button>
                ))}
              </div>
              {items.length > 0 && (
                <Button onClick={handleRefreshInsights} disabled={refreshing} variant="outline" size="sm">
                  {refreshing ? <Loader2 className="size-4 animate-spin" /> : <TrendingUp className="size-4" />}
                  {refreshing ? 'Обновление...' : 'Обновить метрики'}
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {items.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Send className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Публикации не найдены</p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[80px]">Превью</TableHead>
                    <TableHead>Аккаунты</TableHead>
                    <TableHead>Дата</TableHead>
                    <TableHead>ER</TableHead>
                    <TableHead>Метрики</TableHead>
                    <TableHead className="w-[90px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((item) => {
                    const ins = insightsMap[item.mediaId];
                    const comments = commentsLink([item.permalink]);
                    return (
                    <TableRow
                      key={item.mediaId}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => {
                        // Пост — и в адрес, и в сессию: карточка переживёт перезагрузку.
                        stashFeedItem(item);
                        navigate(`/all-publications/${item.mediaId}`, { state: { item } });
                      }}
                    >
                      <TableCell>
                        <MediaThumb
                          url={item.thumbnailUrl}
                          mediaType={item.mediaType}
                          className="w-14 h-14"
                          iconClassName="w-6 h-6"
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{item.username}</span>
                          <PlatformBadge platform={item.accountId.startsWith('tt:') ? 'tiktok' : 'instagram'} />
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Calendar className="w-4 h-4" />
                          {formatDate(item.publishedAt)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{ins ? ins.er.toFixed(2) + '%' : '—'}</span>
                      </TableCell>
                      <TableCell>
                        {item.likes > 0 || item.comments > 0 || ins ? (
                          <div className="flex items-center gap-2 text-sm">
                            <div className="flex items-center gap-1 min-w-[52px]">
                              <Eye className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                              <span className="tabular-nums">{ins ? formatNumber(ins.views) : '—'}</span>
                            </div>
                            <div className="flex items-center gap-1 min-w-[42px]">
                              <Heart className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                              <span className="tabular-nums">{formatNumber(item.likes)}</span>
                            </div>
                            <div className="flex items-center gap-1 min-w-[36px]">
                              <MessageCircle className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                              <span className="tabular-nums">{formatNumber(item.comments)}</span>
                            </div>
                            <div className="flex items-center gap-1 min-w-[36px]">
                              <Share2 className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                              <span className="tabular-nums">{ins ? formatNumber(ins.shares) : '—'}</span>
                            </div>
                            <div className="flex items-center gap-1 min-w-[36px]">
                              <Bookmark className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                              <span className="tabular-nums">{ins ? formatNumber(ins.saved) : '—'}</span>
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          {comments && (
                            <Button
                              variant="ghost"
                              className="h-8 w-8 p-0"
                              title="Разобрать комментарии"
                              aria-label="Разобрать комментарии"
                              onClick={(e) => { e.stopPropagation(); navigate(comments); }}
                            >
                              <MessagesSquare className="h-4 w-4" />
                            </Button>
                          )}
                          <Button asChild variant="ghost" className="h-8 w-8 p-0">
                            <a
                              href={item.permalink}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="Открыть пост"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {(currentPage > 1 || hasNextPage) && (
                <div className="flex flex-wrap items-center justify-between gap-3 mt-6 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1 || isLoading}
                  >
                    <ChevronLeft className="size-4" />
                    Предыдущая
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Страница {currentPage} из {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={!hasNextPage || isLoading}
                  >
                    Следующая
                    <ChevronRight className="size-4" />
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
